import { RESPONSE_PROMISE_SV } from "@/content/response-times";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { confirmationEmail, internalLeadEmail, LEAD_INBOX, LEAD_NOTIFY, sendEmail } from "@/lib/email/resend.server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";
import { captureBlockedLead } from "@/lib/lead-fallback.server";
import { verifyRecaptcha } from "@/lib/recaptcha.server";
import { budgetLabel, timelineLabel, topicLabel } from "@/lib/leads/topics";


export const INTENT_VALUES = ["pulse", "prime", "podd", "live", "post", "annat"] as const;
export const BUDGET_VALUES = ["under-30k", "30-60k", "60-120k", "over-120k", "vet-inte"] as const;
export const TIMELINE_VALUES = ["asap", "1-3-man", "3-6-man", "utforskar"] as const;

const phoneField = z
  .string()
  .trim()
  .max(40)
  .regex(/^[0-9+()\-\s.]{6,40}$/, "Ogiltigt telefonnummer")
  .optional()
  .or(z.literal(""));

const contactSchema = z.object({
  // Steg 1 kräver bara e-post + ärende. Telefon, meddelande och detaljer
  // kompletteras i steg 2 (se completeContact) så tröskeln blir så låg som möjligt.
  name: z.string().trim().max(100).optional().default(""),
  email: z.string().trim().email("Ogiltig e-postadress").max(255),
  phone: phoneField,
  company: z.string().trim().max(150).optional().default(""),
  intent: z.enum(INTENT_VALUES).default("annat"),
  budget: z.enum(BUDGET_VALUES).nullable().optional(),
  timeline: z.enum(TIMELINE_VALUES).nullable().optional(),
  message: z.string().trim().max(2000).optional().default(""),
  token: z.string().min(10).max(4000),
  session_id: z.string().uuid().nullable().optional(),
  visitor_id: z.string().uuid().nullable().optional(),
  source_path: z.string().trim().max(255).optional().nullable(),
  user_agent: z.string().trim().max(500).optional().nullable(),
});

export const getRecaptchaSiteKey = createServerFn({ method: "GET" }).handler(
  async () => {
    return { siteKey: process.env.RECAPTCHA_SITE_KEY ?? "" };
  },
);

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((input) => contactSchema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (!checkRateLimit({ scope: "contact", keys: [ip, data.email], limit: 5, windowMs: 60 * 60 * 1000 })) {
      await captureBlockedLead({ flow: "contact", reason: "rate-limit",
        email: data.email,
        name: data.name ?? null,
        company: data.company ?? null,
        message: data.message ?? null,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        extra: { intent: data.intent, budget: data.budget, timeline: data.timeline },
      });
      return { ok: false as const, error: "För många försök. Försök igen om en stund." };
    }

    // Blockera bara tydliga bottar. Tekniska fel i verifieringen får aldrig
    // stoppa ett äkta lead — de släpps igenom och loggas i stället.
    const captcha = await verifyRecaptcha(data.token);
    if (captcha.action === "block") {
      await captureBlockedLead({
        flow: "contact",
        reason: "recaptcha",
        email: data.email,
        name: data.name ?? null,
        company: data.company ?? null,
        message: data.message ?? null,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        extra: { intent: data.intent, budget: data.budget, timeline: data.timeline },
      });
      return { ok: false as const, error: "Verifiering misslyckades. Försök igen." };
    }

    // Fallbacks när steg 2 utelämnats — namn/meddelande kan vara tomt.
    const displayName = data.name?.trim() || data.email;
    const messageForEmail = data.message?.trim() || "(inget meddelande — kort prisförfrågan)";

    // Persist submission first so the lead is visible in admin even if mail fails.
    let leadId: string | null = null;
    try {
      const { data: inserted, error: insertErr } = await supabaseAdmin
        .from("contact_submissions")
        .insert({
          name: data.name || "",
          email: data.email,
          phone: data.phone || null,
          company: data.company || null,
          intent: data.intent,
          budget: data.budget ?? null,
          timeline: data.timeline ?? null,
          message: data.message || "",
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
          source_path: data.source_path ?? null,
          user_agent: data.user_agent ?? null,
        })
        .select("id")
        .single();
      if (insertErr) console.error("[contact] insert failed:", insertErr);
      leadId = inserted?.id ?? null;
    } catch (e) {
      console.error("[contact] insert unexpected:", e);
    }

    // Notify Fenny + confirmation to the lead (non-blocking).
    const internal = internalLeadEmail({
      subject: `Ny lead (${data.intent}) — ${displayName}`,
      heading: `Ny lead via kontaktformuläret`,
      fields: {
        "Namn": data.name || "—",
        "E-post": data.email,
        "Telefon": data.phone || "—",
        "Företag": data.company || "—",
        "Intent": topicLabel(data.intent),
        "Budget": budgetLabel(data.budget) ?? "—",
        "Tidsplan": timelineLabel(data.timeline) ?? "—",

        "Meddelande": messageForEmail,
      },
    });
    const confirm = confirmationEmail({
      name: displayName,
      intro: `Tack för att du hörde av dig till Fenny — vi har tagit emot ditt meddelande. ${RESPONSE_PROMISE_SV}`,
      body: data.intent !== "annat" ? `Du frågade om: ${data.intent}.` : undefined,
    });

    await Promise.allSettled([
      sendEmail({
        to: LEAD_NOTIFY, subject: internal.subject, html: internal.html, replyTo: data.email,
        logContext: { kind: "internal_lead", leadSource: "contact", leadEmail: data.email },
      }),
      sendEmail({
        to: data.email, subject: confirm.subject, html: confirm.html, replyTo: LEAD_INBOX,
        logContext: { kind: "lead_confirmation", leadSource: "contact", leadEmail: data.email },
      }),
    ]);

    return { ok: true as const, id: leadId };
  });

/**
 * Steg 2: besökaren kompletterar ett redan skickat lead med telefon, namn,
 * företag, budget och tidsplan. Vi uppdaterar samma rad så säljteamet ser en
 * lead, inte två. Id:t är ett slumpat uuid som bara den som skickade får.
 */
const completeSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().max(100).optional().default(""),
  phone: phoneField,
  company: z.string().trim().max(150).optional().default(""),
  budget: z.enum(BUDGET_VALUES).nullable().optional(),
  timeline: z.enum(TIMELINE_VALUES).nullable().optional(),
  message: z.string().trim().max(2000).optional().default(""),
});

export const completeContact = createServerFn({ method: "POST" })
  .inputValidator((input) => completeSchema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (!checkRateLimit({ scope: "contact-complete", keys: [ip, data.id], limit: 10, windowMs: 60 * 60 * 1000 })) {
      return { ok: false as const, error: "För många försök. Försök igen om en stund." };
    }

    const { data: existing, error: readErr } = await supabaseAdmin
      .from("contact_submissions")
      .select("id, email, name, message, created_at")
      .eq("id", data.id)
      .single();
    if (readErr || !existing) {
      return { ok: false as const, error: "Kunde inte hitta förfrågan." };
    }

    const patch: {
      name?: string;
      phone?: string;
      company?: string;
      budget?: string;
      timeline?: string;
      message?: string;
    } = {};
    if (data.name) patch.name = data.name;
    if (data.phone) patch.phone = data.phone;
    if (data.company) patch.company = data.company;
    if (data.budget) patch.budget = data.budget;
    if (data.timeline) patch.timeline = data.timeline;
    if (data.message) {
      patch.message = existing.message?.trim()
        ? `${existing.message}\n\n${data.message}`
        : data.message;
    }
    if (Object.keys(patch).length === 0) return { ok: true as const };

    const { error: updErr } = await supabaseAdmin
      .from("contact_submissions")
      .update(patch)
      .eq("id", data.id);
    if (updErr) {
      console.error("[contact] complete failed:", updErr);
      return { ok: false as const, error: "Kunde inte spara detaljerna." };
    }

    const internal = internalLeadEmail({
      subject: `Komplettering — ${data.name || existing.name || existing.email}`,
      heading: "Besökaren kompletterade sin förfrågan",
      fields: {
        "E-post": existing.email,
        "Namn": data.name || existing.name || "—",
        "Telefon": data.phone || "—",
        "Företag": data.company || "—",
        "Budget": budgetLabel(data.budget) ?? "—",
        "Tidsplan": timelineLabel(data.timeline) ?? "—",
        "Tillägg": data.message || "—",
      },
    });
    await Promise.allSettled([
      sendEmail({
        to: LEAD_NOTIFY, subject: internal.subject, html: internal.html, replyTo: existing.email,
        logContext: { kind: "internal_lead", leadSource: "contact-complete", leadEmail: existing.email },
      }),
    ]);

    return { ok: true as const };
  });
